import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Chip,
    TablePagination,
    IconButton,
    Tooltip,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InfoOutlined, ArrowUpward, ArrowDownward} from '@mui/icons-material';
import {FsCategoryService} from '@/services/accountService';
import {InputField, PopoverButton, SnackbarField} from '@/components/common';
import FsCategoryDetail from '@/pages/components/manage/category/FsCategoryDetail';
import {
    defaultOpenIdsForLevels,
    flattenTree,
    getVisibleTreeRows,
    treeLeadingCellSx,
    treeLevelRowBg,
} from '@/utils/treeTableHelpers';
import {TreeExpandCell} from '@/utils/TreeExpandCell';
import {usePageCrud} from '@/hooks/usePageCrud';

const getRowId = (node: FsCategoryService.FsCategory) =>
    (node.fsId ?? (node as { categoryId?: number }).categoryId)!;

const yesNoChip = (v?: number) => v === 1 ? <Chip size="small" color="success" label="Yes"/> : <Chip size="small" variant="outlined" label="No"/>;

const FsCategoryList: React.FC = () => {
    const theme = useTheme();
    const [tree, setTree] = useState<FsCategoryService.FsCategory[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
        {open: false, message: '', severity: 'success'},
    );
    const [filterCode, setFilterCode] = useState('');
    const [filterENName, setFilterENName] = useState('');
    const [filterZHName, setFilterZHName] = useState('');
    const [filterStatement, setFilterStatement] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState<number | undefined>(undefined);
    const {canRead, canCreate, canUpdate, canRemove} = usePageCrud({
        read: ['fs_category_list', 'fs_category_get', 'fs_category_tree', 'fs_category_page_tree'],
        create: 'account:fs_category:create',
        update: 'account:fs_category:update',
        remove: 'account:fs_category:delete',
    });
    const [openRowIds, setOpenRowIds] = useState<number[]>([]);

    // --- Sort mode state ---
    const [sortMode, setSortMode] = useState(false);
    const [sortingTree, setSortingTree] = useState<FsCategoryService.FsCategory[]>([]);
    const originalTreeRef = useRef<FsCategoryService.FsCategory[]>([]);

    const deepCloneTree = (nodes: FsCategoryService.FsCategory[]): FsCategoryService.FsCategory[] =>
        JSON.parse(JSON.stringify(nodes));

    const enterSortMode = () => {
        originalTreeRef.current = deepCloneTree(tree);
        setSortingTree(deepCloneTree(tree));
        setSortMode(true);
    };

    const cancelSortMode = () => {
        setSortingTree([]);
        setSortMode(false);
    };

    const findParentChildren = (
        nodes: FsCategoryService.FsCategory[],
        targetId: number,
        targetParentId: number | null | undefined,
    ): FsCategoryService.FsCategory[] | null => {
        if (targetParentId == null) return nodes;
        for (const node of nodes) {
            if ((node.fsId ?? 0) === targetParentId) return node.children ?? [];
            if (node.children && node.children.length > 0) {
                const found = findParentChildren(node.children, targetId, targetParentId);
                if (found) return found;
            }
        }
        return null;
    };

    const handleMoveUp = (row: any) => {
        const id = row.fsId ?? row.categoryId;
        const parentId = row.parentId;
        setSortingTree(prev => {
            const next = deepCloneTree(prev);
            const siblings = findParentChildren(next, id, parentId);
            if (!siblings) return prev;
            const idx = siblings.findIndex((s: any) => (s.fsId ?? s.categoryId) === id);
            if (idx <= 0) return prev;
            [siblings[idx - 1], siblings[idx]] = [siblings[idx], siblings[idx - 1]];
            return next;
        });
    };

    const handleMoveDown = (row: any) => {
        const id = row.fsId ?? row.categoryId;
        const parentId = row.parentId;
        setSortingTree(prev => {
            const next = deepCloneTree(prev);
            const siblings = findParentChildren(next, id, parentId);
            if (!siblings) return prev;
            const idx = siblings.findIndex((s: any) => (s.fsId ?? s.categoryId) === id);
            if (idx < 0 || idx >= siblings.length - 1) return prev;
            [siblings[idx], siblings[idx + 1]] = [siblings[idx + 1], siblings[idx]];
            return next;
        });
    };

    const collectReorderItems = (nodes: FsCategoryService.FsCategory[]): FsCategoryService.ReorderItem[] => {
        const items: FsCategoryService.ReorderItem[] = [];
        const walk = (list: FsCategoryService.FsCategory[]) => {
            list.forEach((node, idx) => {
                if (node.fsId != null) items.push({fsId: node.fsId, sortOrder: idx + 1});
                if (node.children && node.children.length > 0) walk(node.children);
            });
        };
        walk(nodes);
        return items;
    };

    const handleConfirmSort = async () => {
        try {
            const items = collectReorderItems(sortingTree);
            await FsCategoryService.reorderCategories(items);
            setSnackbar({open: true, message: 'Sort order updated', severity: 'success'});
            setSortMode(false);
            setSortingTree([]);
            fetchData();
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Reorder failed', severity: 'error'});
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await FsCategoryService.getCategoryTree({
                code: filterCode || undefined,
                nameEn: filterENName || undefined,
                nameZh: filterZHName || undefined,
                statement: filterStatement || undefined,
                page: page + 1,
                size: rowsPerPage
            });
            let nextTree: FsCategoryService.FsCategory[] = [];
            if (Array.isArray(res)) {
                nextTree = res || [];
                setTree(nextTree);
                setTotal(nextTree.length);
            } else if ((res as any)?.items) {
                nextTree = (((res as any).items as any[]) || []) as FsCategoryService.FsCategory[];
                setTree(nextTree);
                setTotal(Number((res as any).total) || 0);
            } else if ((res as any)?.list) {
                nextTree = (((res as any).list as any[]) || []) as FsCategoryService.FsCategory[];
                setTree(nextTree);
                setTotal(Number((res as any).totalRow) || 0);
                const serverPageSize = Number((res as any).pageSize) || rowsPerPage;
                const serverPageNumber = Number((res as any).pageNumber);
                if (serverPageSize !== rowsPerPage) setRowsPerPage(serverPageSize);
                if (!Number.isNaN(serverPageNumber) && serverPageNumber - 1 !== page) setPage(serverPageNumber - 1);
            } else {
                setTree([]);
                setTotal(0);
            }
            // Default: show 一级 + 二级 (open level-0 parents)
            setOpenRowIds(defaultOpenIdsForLevels(nextTree, getRowId, 1));
            setError(null);
        } catch (e: any) {
            const msg = e?.message || 'Load failed';
            setError(msg);
            setSnackbar({open: true, message: msg, severity: 'error'});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCode, filterENName, filterZHName, filterStatement, page, rowsPerPage]);

    const handleDelete = async (id?: number) => {
        if (!id) return;
        try {
            await FsCategoryService.deleteCategory(id);
            setSnackbar({open: true, message: 'Deleted', severity: 'success'});
            fetchData();
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Delete failed', severity: 'error'});
        }
    };

    const handleToggleRow = (id: number) => {
        setOpenRowIds((prev) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const activeTree = sortMode ? sortingTree : tree;

    if (loading) return <CircularProgress sx={{display: 'block', mx: 'auto', my: 4}}/>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Box sx={{
            p: 2,
            bgcolor: 'transparent',
            height: 'calc(100vh - 120px)',
            minHeight: '70vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                   alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                <Typography variant="h6" fontWeight={700} color="text.primary">FS Category</Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{xs: 'stretch', sm: 'center'}}>
                    {sortMode ? (
                        <>
                            <Button variant="contained" color="primary"
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    onClick={handleConfirmSort}>Confirm</Button>
                            <Button variant="outlined" color="inherit"
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    onClick={cancelSortMode}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            <InputField size="small" label="Filter Code" value={filterCode} onChange={e => {
                                setPage(0);
                                setFilterCode(e.target.value);
                            }}/>
                            <InputField size="small" label="Filter Name(EN)" value={filterENName} onChange={e => {
                                setPage(0);
                                setFilterENName(e.target.value);
                            }}/>
                            <InputField size="small" label="Filter Name(ZH)" value={filterZHName} onChange={e => {
                                setPage(0);
                                setFilterZHName(e.target.value);
                            }}/>
                            <InputField size="small" label="Statement" value={filterStatement} onChange={e => {
                                setPage(0);
                                setFilterStatement(e.target.value);
                            }} sx={{minWidth: 160}}/>
                            {canCreate && (
                            <Button variant="contained" color="secondary"
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    onClick={() => {
                                        setEditId(undefined);
                                        setDialogOpen(true);
                                    }}>ADD</Button>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    boxShadow: theme.shadows[2],
                    bgcolor: 'background.paper',
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        width: '100%',
                        tableLayout: 'auto',
                        '& .MuiTableCell-stickyHeader': {
                            bgcolor: 'background.paper',
                            zIndex: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: '4%', minWidth: 40, p: 0}}></TableCell>
                            <TableCell sx={{width: '7%', minWidth: 60, fontWeight: 700}}>Statement</TableCell>
                            <TableCell sx={{width: '12%', minWidth: 90, fontWeight: 700}}>Code</TableCell>
                            <TableCell sx={{width: '26%', minWidth: 150, fontWeight: 700}}>Name (EN)</TableCell>
                            <TableCell sx={{width: '14%', minWidth: 100, fontWeight: 700}}>Name (ZH)</TableCell>
                            <TableCell sx={{width: '10%', minWidth: 80, fontWeight: 700}}>Schedule</TableCell>
                            <TableCell sx={{width: '7%', minWidth: 60, fontWeight: 700}} align="center">Active</TableCell>
                            <TableCell sx={{width: '20%', minWidth: 160, fontWeight: 700}} align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(() => {
                            const flat = flattenTree(activeTree || [], getRowId);
                            const visible = getVisibleTreeRows(flat, openRowIds);
                            if (!visible.length) {
                                return (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">No data</TableCell>
                                    </TableRow>
                                );
                            }
                            return visible.map((r: any) => {
                                const rowId = r.fsId ?? r.categoryId;
                                // Determine sibling boundaries for up/down button disable
                                let isFirst = false;
                                let isLast = false;
                                if (sortMode) {
                                    const siblings = findParentChildren(sortingTree, rowId, r.parentId);
                                    if (siblings) {
                                        const idx = siblings.findIndex((s: any) => (s.fsId ?? s.categoryId) === rowId);
                                        isFirst = idx <= 0;
                                        isLast = idx >= siblings.length - 1;
                                    }
                                }
                                return (
                                    <TableRow key={rowId} hover sx={{bgcolor: treeLevelRowBg(r.level, theme)}}>
                                        <TableCell sx={{p: 0.5, ...treeLeadingCellSx(r.level)}}>
                                            <TreeExpandCell
                                                hasChildren={!!(r.children && r.children.length > 0)}
                                                isOpen={openRowIds.includes(rowId)}
                                                onToggle={() => handleToggleRow(rowId)}
                                            />
                                        </TableCell>
                                        <TableCell sx={treeLeadingCellSx(r.level)}>{r.statement || '-'}</TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...treeLeadingCellSx(r.level)}}>{r.code || '-'}</TableCell>
                                        <TableCell sx={{fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                            {r.nameEn || '-'}
                                        </TableCell>
                                        <TableCell>{r.nameZh || '-'}</TableCell>
                                        <TableCell>{r.scheduleName || '-'}</TableCell>
                                        <TableCell align="center">{yesNoChip(r.isActive)}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                {sortMode ? (
                                                    <>
                                                        <Tooltip title="Move up">
                                                            <span>
                                                                <IconButton size="small" disabled={isFirst}
                                                                            onClick={() => handleMoveUp(r)}>
                                                                    <ArrowUpward fontSize="small"/>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Move down">
                                                            <span>
                                                                <IconButton size="small" disabled={isLast}
                                                                            onClick={() => handleMoveDown(r)}>
                                                                    <ArrowDownward fontSize="small"/>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>
                                                        {canRead && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                minWidth: 72,
                                                                textTransform: 'none',
                                                                borderColor: theme.palette.secondary.main,
                                                                color: theme.palette.secondary.main,
                                                                fontWeight: 600,
                                                                '&:hover': {
                                                                    borderColor: theme.palette.secondary.dark,
                                                                    color: theme.palette.secondary.dark,
                                                                },
                                                            }}
                                                            startIcon={<InfoOutlined/>}
                                                            onClick={() => {
                                                                setEditId(rowId);
                                                                setDialogOpen(true);
                                                            }}
                                                        >
                                                            Detail
                                                        </Button>
                                                        )}
                                                        {canUpdate && (
                                                        <Button size="small" variant="outlined" color="info"
                                                                sx={{textTransform: 'none', fontWeight: 600}}
                                                                onClick={enterSortMode}>Sort</Button>
                                                        )}
                                                        {canRemove && (
                                                        <PopoverButton
                                                            nodeId={rowId}
                                                            nodeName={r.nameEn || r.code || String(rowId)}
                                                            onRemove={handleDelete}
                                                            theme={theme}
                                                        />
                                                        )}
                                                    </>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            });
                        })()}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{flexShrink: 0}}
            />
            <FsCategoryDetail
                open={dialogOpen}
                categoryId={editId}
                onClose={(p) => {
                    setDialogOpen(false);
                    setEditId(undefined);
                    if (p?.message) setSnackbar({open: true, message: p.message, severity: p.severity || 'success'});
                    if (p?.refreshed) fetchData();
                }}
            />
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                autoHideDuration={3000}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
        </Box>
    );
};

export default FsCategoryList;
